import type { Artifact } from "../../api/queries";

/** What a re-issue after a reopen asks of the person (D-17, ADR-0228).
 *
 * When a filing's period has been reopened since it was last issued, the next issue is a restatement
 * and the server refuses it without a written reason (`reissue_reason_required`, 422). **Whether a
 * reason is owed is the server's answer** (`filing_state.reissue_reason_needed`), never inferred
 * here from the number of versions: a second issue with no reopen behind it asks for nothing. The
 * bounds below are the server's (`regimes/artifact.py` REISSUE_REASON_MIN and _MAX, repeated by the
 * table's CHECK); the screen enforces them only so the button is not offered to a request that will
 * be refused. The reason is a person's words: never written to browser storage, kept by the server in
 * its own row, outside the hash and the ledger.
 */

export const REISSUE_REASON_MIN = 12;
export const REISSUE_REASON_MAX = 500;

/** The reason as it will be sent: trimmed, or undefined when it is not one. */
export function reasonToSend(reason: string): string | undefined {
  const trimmed = reason.trim();
  return trimmed.length >= REISSUE_REASON_MIN && trimmed.length <= REISSUE_REASON_MAX ? trimmed : undefined;
}

/** What the "Why issued again" cell says: the reason a re-issue after a reopen gave, or nothing. */
export function reissueWords(artifact: Artifact): string {
  const after = artifact.reopen_event_id ? "After a reopen. " : "";
  if (artifact.reissue_reason) return `${after}${artifact.reissue_reason}`;
  if (artifact.reissue_reason_withheld) return `${after}A reason is recorded. Admins and Approvers can read it.`;
  // A version issued with no reopen behind it has no reason to give, and says nothing.
  return "";
}
